import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withPermissions from 'hocs/WithPermissions';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import { TextButton } from 'components/core/Button';
import Card from 'components/core/Card';
import TextField from 'components/core/TextField';
// Permissions
import OnlyWith from 'components/permissions/OnlyWith';
import permissions from 'constants/Permissions';
// Models
import mCustomer from 'models/cpm/customer';
import mParkingCategory from 'models/cpm/parkingCategory';
// Custom
import { cpmCustomerDataShape, cpmParkingCategoryDataShape, errorShape } from 'constants/PropTypesShapes';
import { buildFormErrors, completeFormErrors, cpmCustomerConstraints } from 'utils/Forms';
import { isValidPhoneNumber } from 'utils/Tools';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import Callout from 'components/misc/Callout';
import PhoneNumberInputField from 'components/inputFields/PhoneNumberInputField';
import ParkingCategorySelectField from 'cpmCarparks/assets/parkingCategories/ParkingCategorySelectField';

class CustomerAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        parkingCategoryId, firstname, lastname, email, phone, comments, spaceNumber,
      },
      customer,
      hasPermission,
      onErrorsChange,
      onValidate,
    } = this.props;

    let errors = buildFormErrors({ parkingCategoryId, lastname, email }, cpmCustomerConstraints);
    // Check phone
    if (phone && !isValidPhoneNumber(phone)) {
      errors = completeFormErrors(errors, 'phone', ['isInvalid']);
    }
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(
        mCustomer.serialize(
          {
            parkingCategoryId,
            firstname,
            lastname,
            email,
            phone,
            comments,
            spaceNumber,
          },
          hasPermission,
        ),
        customer,
      );
      return true;
    }
    return false;
  }

  render() {
    const {
      fields: {
        parkingCategoryId, firstname, lastname, email, phone, comments, spaceNumber,
      },
      errors,
      parkingCategories,
      onFieldsChange,
      onGotoConfiguration,
    } = this.props;

    // Permissions
    const {
      pCpm: { parkingCategoryRead, parkingCategoryWrite },
    } = permissions;

    // ❌ Server errors to display by field
    const emailIsAlreadyTaken = _get(errors, 'global', '') === 'addCustomer.emailIsAlreadyTaken' || _get(errors, 'global', '') === 'updateCustomer.emailIsAlreadyTaken';

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          {_isEmpty(mParkingCategory.sortRegularElements(parkingCategories)) && onGotoConfiguration && (
            <OnlyWith
              permissions={{
                needed: `${parkingCategoryRead}&&${parkingCategoryWrite}`,
                permissionsOrContext: 'carparkPermissions',
              }}
            >
              <div className="col-12 pb-3">
                <Callout
                  withInfoIcon
                  action={(
                    <TextButton
                      inherit
                      size="small"
                      label={<LocalizedMessage id="button.configureCarpark" />}
                      onClick={onGotoConfiguration}
                    />
                  )}
                >
                  <LocalizedMessage id="info.addParkingCategoryBeforeUser" />
                </Callout>
              </div>
            </OnlyWith>
          )}
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: <LocalizedMessage id="help.customers.formPhone" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-lg-8">
                      <ParkingCategorySelectField
                        withOnDemand={false}
                        errorText={_get(errors, 'parkingCategoryId')}
                        value={parkingCategoryId}
                        required
                        onChange={(value) => onFieldsChange({ parkingCategoryId: value })}
                      />
                    </div>
                    <div className="col-12">
                      <TextField
                        label={<LocalizedMessage id="field.spaceNumber" />}
                        errorText={_get(errors, 'spaceNumber')}
                        value={spaceNumber}
                        inputMaxWidth={150}
                        onChange={(value) => onFieldsChange({ spaceNumber: value })}
                      />
                    </div>
                    <div className="col-12">
                      <TextField
                        label={<LocalizedMessage id="field.lastname" />}
                        errorText={_get(errors, 'lastname')}
                        value={lastname}
                        required
                        onChange={(value) => onFieldsChange({ lastname: value })}
                      />
                    </div>
                    <div className="col-12">
                      <TextField
                        label={<LocalizedMessage id="field.firstname" />}
                        errorText={_get(errors, 'firstname')}
                        value={firstname}
                        onChange={(value) => onFieldsChange({ firstname: value })}
                      />
                    </div>
                    <div className="col-12">
                      <TextField
                        type="email"
                        name="email"
                        autoComplete="email"
                        label={<LocalizedMessage id="field.email" />}
                        errorText={_get(errors, 'email') || (emailIsAlreadyTaken && <LocalizedMessage id="error.addCustomer.emailIsAlreadyTaken" />)}
                        ﬂ
                        value={email}
                        onChange={(value) => onFieldsChange({ email: value })}
                      />
                    </div>
                    <div className="col-12 col-lg-8">
                      <PhoneNumberInputField
                        labelId="field.phoneNumber"
                        value={phone}
                        errorText={_get(errors, 'phone')}
                        onChange={(value) => onFieldsChange({ phone: value })}
                      />
                    </div>
                    <div className="col-12">
                      <TextField
                        label={<LocalizedMessage id="field.comments" />}
                        errorText={_get(errors, 'comments')}
                        value={comments}
                        onChange={(value) => onFieldsChange({ comments: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

CustomerAddEditForm.propTypes = {
  operation: PropTypes.oneOf(['add', 'update']).isRequired, // eslint-disable-line react/no-unused-prop-types
  customer: cpmCustomerDataShape,
  parkingCategories: PropTypes.arrayOf(cpmParkingCategoryDataShape),
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  hasPermission: PropTypes.func.isRequired,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onGotoConfiguration: PropTypes.func,
};

CustomerAddEditForm.defaultProps = {
  customer: null,
  parkingCategories: null,
  operationError: null,
  errors: null,
  onGotoConfiguration: null,
};

const mapFieldsToProps = {
  parkingCategoryId: {
    resourceKey: 'parkingCategory',
    defaultValue: null,
    setTransform: (parkingCategory) => _get(parkingCategory, 'id'),
  },
  firstname: {
    resourceKey: 'firstname',
    defaultValue: '',
  },
  lastname: {
    resourceKey: 'lastname',
    defaultValue: '',
  },
  email: {
    resourceKey: 'email',
    defaultValue: '',
  },
  phone: {
    resourceKey: 'phone',
    defaultValue: '',
  },
  comments: {
    resourceKey: 'comments',
    defaultValue: '',
  },
  spaceNumber: {
    resourceKey: 'spaceNumber',
    defaultValue: '',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.customer,
  operationError: ownProps.operationError,
});

const mapStateToProps = (state, ownProps) => ({
  operationError: _get(state.cpmCarparkCustomer, [ownProps.operation, 'error']),
  parkingCategories: state.cpmCarparkParkingCategory.list.elements,
});

module.exports = compose(
  withPermissions,
  connect(mapStateToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['addCarparkCustomer', 'updateCarparkCustomer']),
)(CustomerAddEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/customers/CustomerAddEditForm.js